import React from "react";

import { bdtFmt, DetailRow, formatShareQty } from "./InvestorTxnDetailUi";
import RedemptionProgressSteps from "./RedemptionProgressSteps";
import { redemptionStatusLabel, redemptionStatusTone } from "../../utils/redemptionLabels";

function rowGrossBdt(row) {
  return Number(row?.total_bdt || 0);
}

function rowFeeBdt(row) {
  return Number(row?.fee_bdt || 0);
}

function rowNetBdt(row) {
  if (row?.net_cash_bdt != null) return Number(row.net_cash_bdt);
  const g = rowGrossBdt(row);
  const f = rowFeeBdt(row);
  return g > 0 ? g - f : 0;
}

function formatAuditLine(log) {
  const when = log.created_at ? new Date(log.created_at).toLocaleString() : "";
  const transition = [log.from_status, log.to_status].filter(Boolean).join(" → ");
  const parts = [when, transition, log.note].filter(Boolean);
  return parts.join(" · ");
}

export default function RedemptionOfferDetailModal({ row, onClose, onRequestCancel, cancelling }) {
  if (!row) return null;

  const status = String(row.status || "").toUpperCase();
  const isPending = status === "PENDING";
  const approved = row.shares_approved != null ? formatShareQty(row.shares_approved) : null;
  const rejectedShares =
    row.shares_rejected != null && Number(row.shares_rejected) > 0 ? formatShareQty(row.shares_rejected) : null;
  const gross = rowGrossBdt(row);
  const fee = rowFeeBdt(row);
  const net = rowNetBdt(row);
  const trail = Array.isArray(row.audit_trail) ? row.audit_trail : [];
  const showAudit = trail.length > 1;

  return (
    <div className="invx-about-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="invx-about-modal investor-offer-detail"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="redemption-detail-title"
        aria-modal="true"
      >
        <button type="button" className="invx-about-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="investor-offer-detail__header">
          <div className="investor-offer-detail__header-main">
            <p className="investor-offer-detail__eyebrow">Redemption request</p>
            <h3 id="redemption-detail-title" className="investor-offer-detail__title">
              {row.redemption_ref || "Redemption"}
            </h3>
            <p className="investor-offer-detail__project">{row.project_display || "—"}</p>
          </div>
          <span className={`badge ${redemptionStatusTone(row.status)}`}>{redemptionStatusLabel(row.status)}</span>
        </header>

        <div className="investor-offer-detail__progress">
          <RedemptionProgressSteps status={row.status} requestedAt={row.requested_at} processedAt={row.processed_at} />
        </div>

        <div className="investor-offer-detail__panels">
          <section className="investor-offer-detail__panel" aria-labelledby="redemption-detail-request">
            <h4 id="redemption-detail-request" className="investor-offer-detail__panel-title">
              Request
            </h4>
            <DetailRow label="Shares requested">{formatShareQty(row.shares)}</DetailRow>
            {approved ? <DetailRow label="Shares approved">{approved}</DetailRow> : null}
            {rejectedShares ? <DetailRow label="Shares released">{rejectedShares}</DetailRow> : null}
            <DetailRow label="NAV at request">{bdtFmt.format(Number(row.price_per_share || 0))}</DetailRow>
          </section>

          <section className="investor-offer-detail__panel" aria-labelledby="redemption-detail-amounts">
            <h4 id="redemption-detail-amounts" className="investor-offer-detail__panel-title">
              Payout
            </h4>
            <DetailRow label="Gross (est.)" empty={gross <= 0}>
              {gross > 0 ? bdtFmt.format(gross) : null}
            </DetailRow>
            <DetailRow label="Fees" empty={fee <= 0}>
              {fee > 0 ? bdtFmt.format(fee) : null}
            </DetailRow>
            <DetailRow label="Net to wallet" empty={net <= 0}>
              {net > 0 ? bdtFmt.format(net) : null}
            </DetailRow>
          </section>
        </div>

        {showAudit ? (
          <section className="investor-offer-detail__audit" aria-labelledby="redemption-detail-audit">
            <h4 id="redemption-detail-audit" className="investor-offer-detail__panel-title">
              History
            </h4>
            <ul className="investor-offer-detail__audit-list">
              {trail.map((log) => (
                <li key={log.id} className="investor-offer-detail__audit-item">
                  {formatAuditLine(log)}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {row.admin_notes ? (
          <p className="investor-offer-detail__hint muted">
            <strong>Note:</strong> {row.admin_notes}
          </p>
        ) : null}

        {isPending ? (
          <p className="investor-offer-detail__hint muted">
            You can cancel this request while it is pending review. Locked shares will be released.
          </p>
        ) : null}

        <footer className="investor-offer-detail__footer">
          <button type="button" className="btn-secondary compact-btn" onClick={onClose}>
            Close
          </button>
          {isPending && onRequestCancel ? (
            <button
              type="button"
              className="btn-danger compact-btn"
              disabled={cancelling}
              onClick={() => onRequestCancel(row)}
            >
              {cancelling ? "Cancelling…" : "Cancel request"}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}
